import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Option } from '@openx/types/options.ts';
import { type ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FilterCriteria,
  GREATER_LESS_FILTER_OPTIONS,
  type GreaterLessFilter,
  type GreaterLessOptionType,
} from '../../types.ts';

export type GreaterLessSelectorProps<Criteria extends FilterCriteria> = {
  options?: Option[];
  config?: GreaterLessFilter<Criteria>['config'];
  selectedOptions?: string;
  onOptionChange: (optionId: string) => void;
};

export const GreaterLessSelector = <Criteria extends FilterCriteria>({
  options,
  config,
  selectedOptions,
  onOptionChange,
}: GreaterLessSelectorProps<Criteria>) => {
  const { t } = useTranslation();

  const comparisonOptions = [
    { label: t('Greater Than'), value: GREATER_LESS_FILTER_OPTIONS.Greater },
    { label: t('Less Than'), value: GREATER_LESS_FILTER_OPTIONS.Less },
  ] as const;

  const parseSelectedOptions = (input?: string): [string, GreaterLessOptionType, string] => {
    if (!input) return [options?.[0].id || '', GREATER_LESS_FILTER_OPTIONS.Greater, ''];

    const [optId, comp, val] = input.split(';');
    const isValidComparison = comp === GREATER_LESS_FILTER_OPTIONS.Greater || comp === GREATER_LESS_FILTER_OPTIONS.Less;
    const parsedVal = parseInt(val, 10);

    if (!optId || !isValidComparison || Number.isNaN(parsedVal)) {
      return [options?.[0].id || '', GREATER_LESS_FILTER_OPTIONS.Greater, '0'];
    }

    return [optId, comp as GreaterLessOptionType, val];
  };

  const [currentOptionId, setCurrentOptionId] = useState<string>(() => parseSelectedOptions(selectedOptions)[0]);
  const [currentComparison, setCurrentComparison] = useState<GreaterLessOptionType>(
    () => parseSelectedOptions(selectedOptions)[1]
  );
  const [currentValue, setCurrentValue] = useState<string>(() => parseSelectedOptions(selectedOptions)[2]);

  const handleOptionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newId = event.target.value;
      setCurrentOptionId(newId);
      const optionChangeValue = currentValue === '' ? '' : `${newId};${currentComparison};${currentValue}`;
      onOptionChange(optionChangeValue);
    },
    [currentComparison, currentValue, onOptionChange]
  );

  const handleComparisonChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newComp = event.target.value as GreaterLessOptionType;
      setCurrentComparison(newComp);
      const optionChangeValue = currentValue === '' ? '' : `${currentOptionId};${newComp};${currentValue}`;
      onOptionChange(optionChangeValue);
    },
    [currentOptionId, currentValue, onOptionChange]
  );

  const handleValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let newVal = '';
      const parsedVal = parseInt(event.target.value, 10);

      if (event.target.value !== '' && !Number.isNaN(parsedVal)) {
        newVal = `${parsedVal}`;

        if (config?.valueChangeValidator && !config.valueChangeValidator(newVal)) {
          return;
        }
      }

      setCurrentValue(newVal);
      const optionChangeValue = newVal === '' ? '' : `${currentOptionId};${currentComparison};${newVal}`;
      onOptionChange(optionChangeValue);
    },
    [currentOptionId, currentComparison, onOptionChange, config?.valueChangeValidator]
  );

  return (
    <Box>
      {options && options.length > 0 && (
        <FormControl component="fieldset">
          <RadioGroup value={currentOptionId} onChange={handleOptionChange} data-test="greater-less-radio-selector">
            {options?.map(option => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio />}
                label={<Typography>{option.name}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
      <Stack
        direction="row"
        sx={{
          mt: 2,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            height: 32, // Fixed height
            padding: 0,
          }}
        >
          <TextField
            select
            data-test="greater-less-comparison-selector"
            value={currentComparison}
            onChange={handleComparisonChange}
            size="small"
            sx={{
              '> .MuiInputBase-root': {
                border: 'none',
                borderRight: '1px solid',
                borderColor: 'divider',
              },
              '&.MuiFormControl-root': {
                padding: '0px !important',
                border: 'none !important',
              },
              flex: 1,
              px: 2,
              height: '100%',
              borderRight: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
            }}
          >
            {comparisonOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value} data-test={`greater-less-comparison-option-${opt.value}`}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            data-test="greater-less-value-input"
            type="text"
            value={currentValue === '' ? '' : currentValue}
            onChange={handleValueChange}
            size="small"
            placeholder={config?.emptyPlaceHolder || t('Enter the value')}
            sx={{
              '> .MuiInputBase-root': {
                border: 'none',
              },
              '&.MuiFormControl-root': {
                padding: '0px !important',
                border: 'none !important',
              },
              flex: 1,
              px: 2,
              height: '100%',
            }}
            {...(config?.criteriaValueSuffix && {
              slotProps: {
                input: {
                  endAdornment: <InputAdornment position="end">{config.criteriaValueSuffix}</InputAdornment>,
                },
              },
            })}
          />
        </Box>
      </Stack>
    </Box>
  );
};
